import React, { useEffect, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import styled from 'styled-components/native'
import { Feather } from '../../../components/icon/vectorIcons'
import { useMutation } from '@apollo/client'
import Modal from '../../../components/common/Modal'
import TextInput from '../../../components/common/TextInput'
import Dropdown from '../../../components/common/Dropdown'
import AudienceMultiselect from '../../../components/common/form/AudienceMultiselect'
import useTranslation from '../../../hooks/useTranslation'
import useToast, { ToastType } from '../../../hooks/useToast'
import { ShareDocletData, updateShareDocletMetadataMutation } from '../api'
import { builderColors } from './builderColors'
import { PANEL_BODY, PANEL_CONTENT, PANEL_OVERLAY } from './sidePanel'

// DocletType values, in the API's order
export const DOCLET_TYPES = [
  'agenda',
  'briefingBook',
  'highlights',
  'insights',
  'deepDive',
  'engageHighlights',
  'evergreenContent',
  'oneDegree',
  'podcastHighlights',
  'programGuide',
  'recap',
  'roundtableHighlights',
  'theW',
  'next'
]

// Same right-hand panel treatment as Content Curation's SelectContentModal
interface DocumentSettingsPanelProps {
  doclet: ShareDocletData
  /** The first Title block's title, live from the builder */
  titleFromPage: string | null
  open: boolean
  onClose: () => void
  // Asks to delete the doclet (the builder confirms first)
  onDelete: () => void
}

/**
 * Doclet metadata: name, type and communities. These are metadata only, so
 * each change saves immediately rather than going through the draft.
 *
 * The name defaults to the page title (the first Title block's title): until a
 * name is set, the field shows that title, and editing it sets a name of its
 * own. Clearing the field, or "Use page title", goes back to following the
 * title.
 */
const DocumentSettingsPanel = ({
  doclet,
  titleFromPage,
  open,
  onClose,
  onDelete
}: DocumentSettingsPanelProps) => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [update] = useMutation(updateShareDocletMetadataMutation)
  const followsTitle = !doclet.name
  const shownName = doclet.name ?? titleFromPage ?? ''
  const [name, setName] = useState(shownName)

  useEffect(() => {
    if (open) setName(shownName)
  }, [open, shownName])

  const save = async (values: Record<string, unknown>) => {
    try {
      await update({ variables: { id: doclet.id, ...values } })
    } catch {
      showToast(t('doclets:settings:saveFailed'), ToastType.ERROR)
    }
  }

  const saveName = () => {
    const trimmed = name.trim()
    if (followsTitle) {
      // Unchanged from the page title (or still blank): keep following it
      if (!trimmed || trimmed === titleFromPage) return
      save({ name: trimmed })
    } else if (trimmed !== doclet.name) {
      // Blank goes back to following the page title
      save({ name: trimmed || null })
    }
  }

  const usePageTitle = () => {
    setName(titleFromPage ?? '')
    save({ name: null })
  }

  const close = () => {
    saveName()
    onClose()
  }

  const typeOptions = DOCLET_TYPES.map(type => ({
    value: type,
    label: t(`doclets:types:${type}`)
  }))

  return (
    <Modal
      open={open}
      close={close}
      overlayStyle={PANEL_OVERLAY}
      contentStyle={PANEL_CONTENT}
      bodyStyle={PANEL_BODY}
    >
      <Header>
        <Title>{t('doclets:settings:title')}</Title>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('doclets:settings:close')}
          onPress={close}
        >
          <Feather name="x" size={20} color={builderColors.text} />
        </Pressable>
      </Header>
      <HelpText>{t('doclets:settings:help')}</HelpText>

      <Field>
        <Label>{t('doclets:settings:name')}</Label>
        <TextInput
          placeholder={t('doclets:untitled')}
          value={name}
          onChangeText={setName}
          onBlur={saveName}
        />
        {followsTitle && titleFromPage ? (
          <FieldHelp>{t('doclets:settings:nameFromTitle')}</FieldHelp>
        ) : null}
        {!followsTitle && titleFromPage && name.trim() !== titleFromPage ? (
          <Pressable
            accessibilityRole="button"
            onPress={usePageTitle}
            style={{ alignSelf: 'flex-start', marginTop: 6 }}
          >
            <LinkText>{t('doclets:settings:usePageTitle')}</LinkText>
          </Pressable>
        ) : null}
      </Field>

      <Field style={{ zIndex: 2 }}>
        <Label>{t('doclets:settings:type')}</Label>
        <Dropdown
          options={typeOptions}
          value={doclet.type}
          placeholder={t('doclets:settings:typePlaceholder')}
          onSelect={type => save({ type: type ?? null })}
          isClearable
        />
      </Field>

      <Field style={{ zIndex: 1 }}>
        <AudienceMultiselect
          placeholder={t('doclets:settings:communities')}
          value={doclet.communities.map(community => community.id)}
          onSelect={communityIds => save({ communityIds })}
          onBlur={() => {}}
          hasError={false}
        />
        <HelpText>{t('doclets:settings:communitiesHelp')}</HelpText>
      </Field>

      <DangerZone>
        <Label>{t('doclets:settings:deleteLabel')}</Label>
        <FieldHelp style={{ marginTop: 0, marginBottom: 10 }}>
          {t('doclets:settings:deleteHelp')}
        </FieldHelp>
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            saveName()
            onDelete()
          }}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'flex-start',
            paddingVertical: 6,
            paddingHorizontal: 10,
            borderWidth: 1,
            borderColor: builderColors.danger,
            borderRadius: 6
          }}
        >
          <Feather name="trash-2" size={14} color={builderColors.danger} />
          <DangerText>{t('doclets:settings:delete')}</DangerText>
        </Pressable>
      </DangerZone>
    </Modal>
  )
}

const Header = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const Title = styled(Text)`
  font-size: 22px;
  font-weight: 700;
  color: ${builderColors.text};
`

const HelpText = styled(Text)`
  font-size: 13px;
  line-height: 18px;
  color: ${builderColors.mutedText};
  margin-bottom: 16px;
`

const FieldHelp = styled(Text)`
  font-size: 12px;
  line-height: 17px;
  color: ${builderColors.mutedText};
  margin-top: 6px;
`

const LinkText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: ${builderColors.tag};
`

const Field = styled(View)`
  margin-bottom: 16px;
`

// Set apart at the bottom of the panel, below the everyday settings
const DangerZone = styled(View)`
  margin-top: auto;
  padding-top: 16px;
  border-top-width: 1px;
  border-top-color: ${builderColors.border};
`

const DangerText = styled(Text)`
  margin-left: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${builderColors.danger};
`

const Label = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${builderColors.text};
  margin-bottom: 6px;
`

export default DocumentSettingsPanel
